import { FC } from 'react';

import { Visibility } from '@mui/icons-material';
import { createInvestmentViewLink } from 'adminConstants/routes';
import { useGetRole } from 'hooks/ui/useGetRole';
import SearchField from 'components/SearchField';
import Table, { TableActions } from 'components/Table';
import TooltipTotalValue from 'components/TooltipTotalValue';
import { Api } from 'further-types/adviser';
import { PaginationState } from 'further-types/utils';
import { dateToLabel, numberToCurrencyString } from 'further-ui/utils';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  title: {
    color: '#656565',
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '2.5rem',
    marginBottom: '1.5rem',
  },
}));

type Props = {
  linkedInvestments: {
    data: Api.AdviserSubscriptionsResponse;
    isFetching: boolean;
  };
  handleSearch: (query: string) => void;
  pagination: PaginationState;
};

const LinkedInvestments: FC<Props> = ({
  linkedInvestments: { data, isFetching },
  handleSearch,
  pagination,
}) => {
  const { classes } = useStyles();
  const { isSuperAdmin } = useGetRole();

  const columns = [
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            render: (elm: any) => elm?.firmId?.firmName,
            key: 'firmId.firmName',
          },
        ]
      : []),
    {
      label: 'Investor',
      key: 'fullName',
    },
    {
      label: 'Subscription date',
      key: 'investmentDate',
      render: (elm: Api.AdviserSubscription) => dateToLabel(elm.investmentDate),
    },
    {
      label: 'Adviser name',
      key: 'adviserName',
      render: (elm: Api.AdviserSubscription) => elm?.adviserId?.fullName,
      sort: false,
    },
    {
      label: 'Advice fee',
      key: 'adviserFee',
      render: (elm: Api.AdviserSubscription) =>
        numberToCurrencyString(elm.adviserFee ?? 0),
    },
    {
      label: 'Initial subscription',
      key: 'investmentAmount',
      render: (elm: Api.AdviserSubscription) =>
        numberToCurrencyString(elm.investmentAmount),
    },
    {
      label: 'Unrealised value',
      key: 'investmentBalancePropertyCache.unrealisedValue.total',
      render: (elm: Api.AdviserSubscription) =>
        numberToCurrencyString(
          elm.investmentBalancePropertyCache?.unrealisedValue.total,
        ),
    },
    {
      label: 'Realised value',
      key: 'investmentBalancePropertyCache.realisedValue.total',
      render: (elm: Api.AdviserSubscription) =>
        numberToCurrencyString(
          elm.investmentBalancePropertyCache?.realisedValue.total,
        ),
    },
    {
      label: <TooltipTotalValue />,
      key: 'investmentBalancePropertyCache.investmentValue',
      render: (elm: Api.AdviserSubscription) =>
        numberToCurrencyString(
          elm.investmentBalancePropertyCache?.investmentValue,
        ),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm: Api.AdviserSubscription) => (
        <TableActions
          actions={[
            {
              label: 'View subscription',
              icon: Visibility,
              color: 'primary',
              link: createInvestmentViewLink(elm?._id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <div className={classes.flex}>
        <h2 className={classes.title}>Linked subscriptions</h2>
        <SearchField
          onSearch={handleSearch}
          autoSearchOnDebounce
          allowEmptyQuery
          variant="outlined"
          name="investorName"
          id="investorName"
          placeholder="Search by investor name"
          width={'20rem'}
          fullWidth
        />
      </div>

      <Table
        columns={columns}
        tablebody={data?.subscriptions ?? []}
        loading={isFetching}
        onRequestSort={pagination.handleRequestSort}
        order={pagination.order}
        orderBy={pagination.orderBy}
        page={pagination.page}
        onPageChange={pagination.handleChangePage}
        count={data?.totals.investments ?? 0}
        pagination={true}
        rowsPerPage={pagination.rowsPerPage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        emptyMessage="No subscriptions found"
      />
    </>
  );
};

export default LinkedInvestments;
